/* eslint-disable @typescript-eslint/consistent-type-assertions */
import {
  ECatalogItemIdentifierType,
  IShopConfig,
  ShopConfigData,
  TDestinationSetting,
} from '@/core/apis/config_api/config_api_types';
import {
  CURR_ENV,
  IS_DEBUG,
  USE_TEST_DESTINATIONS,
} from '@/core/constants/appConfig/environment';
import { EScriptEnv } from '@/core/constants/appConfig/types';
import { TEventMetaData } from '@/core/constants/event_types/base_mable_event';
import { EMableEventName } from '@/core/constants/event_types/events';
import { EDestinationType } from '@/core/interfaces';
import { BusinessLogicSettings } from '@/core/types';
import { Logger } from '@/shared/logger/logger';

import { TConsentMap } from '../consent/types';
import { TEST_DESTINATIONS } from './test_destinations';

type LocalShopConfigSettings = {
  [key: string]: any;
  cart_attributes_enabled: boolean;
  line_items_enabledd: boolean;
  shpy_cartTokenSyncDisabled: boolean;
};

const validShopConfigKeys = new Set([
  'shpy_cartTokenSyncDisabled',
  'line_items_enabledd',
  'cart_attributes_enabled',
  'is_test',
  'debug_mode',
  'wgs_debug_mode',
  'wgs_log_level',
  'wgs_custom_domain',
  'shopify_app_proxy_enabled',
]);

export class ShopConfigHandler implements IShopConfig {
  shopConfigData: ShopConfigData;

  constructor(
    shopConfigData: ShopConfigData,
    localShopConfig?: Map<string, any>,
  ) {
    this.shopConfigData = shopConfigData;
    if (localShopConfig) {
      const localShopConfigSettings = {} as LocalShopConfigSettings;
      for (const key of localShopConfig.keys()) {
        if (validShopConfigKeys.has(key)) {
          localShopConfigSettings[key] = localShopConfig.get(key);
        }
      }
      this.shopConfigData.config = {
        ...this.shopConfigData.config,
        ...localShopConfigSettings,
        debug_mode: IS_DEBUG,
      };
    }
    try {
      if (
        shopConfigData.config.wgs_custom_domain &&
        new URL(shopConfigData.config.wgs_custom_domain).hostname
      ) {
        Logger.setWgsCustomDomain(shopConfigData.config.wgs_custom_domain);
      }
    } catch (error) {
      Logger.error('Error setting custom domain', error);
    }
    this.shopConfigData.customEvents = this.shopConfigData.customEvents || [];
  }
  isPerDestinationConsentActive = (): boolean =>
    this.shopConfigData.config.perDestinationConsentActive || false;
  getCustomEventConfigs(): any {
    return this.shopConfigData?.customEvents || [];
  }
  getDestinations(): TDestinationSetting[] {
    // TODO
    const destinations: TDestinationSetting[] =
      this.shopConfigData.destinations.concat([
        {
          active: true,
          creds: {
            catalog_item_identifier_type: ECatalogItemIdentifierType.VARIANT_ID,
          }, // should contain unique entries
          id: '0',
          type: EDestinationType.Mable,
        } satisfies TDestinationSetting,
      ]);
    if (CURR_ENV === EScriptEnv.TEST) {
      return TEST_DESTINATIONS;
    } else if (
      [EScriptEnv.LOCAL, EScriptEnv.TEST].includes(CURR_ENV) &&
      USE_TEST_DESTINATIONS
    ) {
      destinations.push(
        {
          active: true,
          creds: {
            catalog_item_identifier_type: ECatalogItemIdentifierType.VARIANT_ID,
            measurementId: 'G-BGP6D0TXX7',
          }, // should contain unique entries
          id: '3',
          type: EDestinationType.GA4,
        },
        {
          active: true,
          creds: {
            catalog_item_identifier_type: ECatalogItemIdentifierType.VARIANT_ID,
            container_id: 'GTM-KXX7MM75',
            sgtm_container_url: 'https://gtm-server.int.mable.ai',
          },
          id: '12',
          type: EDestinationType.WSGTM,
        } satisfies TDestinationSetting,
        {
          active: true,
          creds: {
            catalog_item_identifier_type: ECatalogItemIdentifierType.VARIANT_ID,
            pixelId: '1471314667031484',
          }, // should contain unique entries
          id: '5',
          type: EDestinationType.Facebook,
        },
        {
          active: true,
          creds: {
            catalog_item_identifier_type: ECatalogItemIdentifierType.VARIANT_ID,
            containerId: 'GTM-59T8ZBM',
          },
          id: '1',
          type: EDestinationType.GTM,
        },
        {
          active: true,
          creds: {
            adwordsId: 'AW-475399036',
            catalog_item_identifier_type: ECatalogItemIdentifierType.VARIANT_ID,
            conversionEventsMap: {
              add_to_cart: 'b3pICI7X4vUYEPyG2OIB',
            },
            remarketingEventsDisabled: false,
          },
          id: '2',
          type: EDestinationType.GoogleAds,
        },
      );
    }
    return destinations;
  }
  // implement a function to calculate event origin (eo) - scriptTag_bundleName
  getMetaData(): TEventMetaData {
    const eo = `scriptTag_${Logger.bundleName || 'example-bundle'}`;
    return {
      duids: [],
      eo,
      pv: '0.0.1',
      session_found: true,
      suid: this.shopConfigData.uid,
      syst: this.shopConfigData.sourceType,
    };
  }

  getConsentSettings(): TConsentMap {
    // TODO: Do we need consent map?
    return {} as any;
  }

  eventNeedsBusinessLogicApplied(
    type: EDestinationType,
    eventName: EMableEventName,
  ): boolean {
    return (
      eventName === EMableEventName.ORDER_COMPLETED &&
      type !== EDestinationType.Mable
    );
  }

  getBusinessLogicSettings(name: string): BusinessLogicSettings {
    Logger.info('getBusinessLogicSettings', name);
    return {} as BusinessLogicSettings;
  }
  getShopConfigData(): ShopConfigData {
    return this.shopConfigData;
  }
}
